import React from 'react';
import { useSelector } from 'react-redux';
import VodButton from '../../../../shared/VodButton';
import ContentShareButtons from './ContentShareButtons';
import ContentKindBadge from './ContentKindBadge';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';

import {
  formatDateTime,
  resolveAiringDate,
  resolveAudioUrl,
  resolveContentImage,
  resolveEffectiveDate,
  resolveHeroVisual,
  resolvePublicPageUrl,
  resolveProgramTitle,
  resolveImageCredit,
  shouldShowDownloadButton,
  shouldShowListenButton,
} from '../helpers';

type ContentHeroProps = {
  content: any;
};

export default function ContentHero({ content }: ContentHeroProps) {
  const title = content?.title || '';
  const description = content?.description || '';
  const programTitle = resolveProgramTitle(content);
  const rawCover = resolveContentImage(content);
  const rawHeroVisual = resolveHeroVisual(content);
  const imageCredit = resolveImageCredit(content);
  const cover = rawCover ? flattenToAppURL(rawCover) : '';
  const heroVisual = rawHeroVisual ? flattenToAppURL(rawHeroVisual) : '';
  const playerCover = cover || heroVisual;
  const airingDate = resolveAiringDate(content);
  const effectiveDate = resolveEffectiveDate(content);
  const referenceDate = airingDate || effectiveDate;
  const referenceLabel = airingDate ? 'No ar em' : 'Publicado em';
  const audioUrl = resolveAudioUrl(content);
  const pageUrl = resolvePublicPageUrl(content);

  const playerMode = useSelector((state: any) => state.player?.mode ?? '');
  const currentVodUrl = useSelector((state: any) => state.player?.vodUrl || '');
  const intentPlaying = useSelector(
    (state: any) => !!state.player?.intentPlaying,
  );

  const isPlayingThis =
    !!audioUrl &&
    playerMode === 'vod' &&
    currentVodUrl === audioUrl &&
    intentPlaying;

  const showListen = shouldShowListenButton(content);
  const showDownload = shouldShowDownloadButton(content);

  return (
    <section
      className={[
        'content-view__hero',
        !cover ? 'content-view__hero--no-image' : '',
        isPlayingThis ? 'content-view__hero--playing' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-labelledby="content-title"
      style={
        heroVisual
          ? {
              backgroundImage: `linear-gradient(180deg, rgba(13, 24, 28, 0.70) 0%, rgba(13, 24, 28, 0.94) 55%, rgba(13, 24, 28, 1) 100%), url(${heroVisual})`,
            }
          : undefined
      }
    >
      <div className="content-view__hero-inner">
        <div className="content-view__hero-copy">
          <div className="content-view__hero-topline">
            <ContentKindBadge content={content} />

            {programTitle ? (
              <p className="content-view__program-name">{programTitle}</p>
            ) : null}
          </div>

          <h1 id="content-title" className="content-view__title">
            {title}
          </h1>

          {description ? (
            <p className="content-view__description">{description}</p>
          ) : null}

          {referenceDate ? (
            <div className="content-view__airing">
              <span className="content-view__airing-label">
                {referenceLabel}
              </span>
              <time
                className="content-view__airing-date"
                dateTime={referenceDate}
              >
                {formatDateTime(referenceDate)}
              </time>
            </div>
          ) : null}

          {isPlayingThis ? (
            <div className="content-view__nowPlaying" aria-live="polite">
              <span className="content-view__equalizer" aria-hidden="true">
                <i />
                <i />
                <i />
              </span>
              <span>Tocando agora</span>
            </div>
          ) : null}

          <div
            className="content-view__actions"
            role="group"
            aria-label="Ações do conteúdo"
          >
            {showListen && audioUrl ? (
              <VodButton
                url={audioUrl}
                title={title}
                subtitle={programTitle}
                image={playerCover}
                label="Ouça agora"
                origin="ContentView"
                analytics={{
                  event: 'audio_play',
                  section: 'content.hero',
                  contentId: String(content?.UID || content?.id || ''),
                  contentType: content?.audio_type?.token || 'vod',
                  label: 'Ouça agora',
                }}
                className={
                  isPlayingThis
                    ? 'content-view__action pressable content-view__action--listen vodButton--active'
                    : 'content-view__action pressable content-view__action--listen'
                }
              />
            ) : null}

            {showDownload && audioUrl ? (
              <a
                href={audioUrl}
                className="content-view__action content-view__action--download"
                download
                aria-label={`Baixar áudio de ${title}`}
                title={`Baixar áudio de ${title}`}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M12 3v12" />
                  <path d="M7 10l5 5 5-5" />
                  <path d="M5 21h14" />
                </svg>

                <span>Baixar</span>
              </a>
            ) : null}

            <ContentShareButtons
              title={title}
              url={pageUrl}
              className="content-view__share--hero"
            />
          </div>
        </div>

        {cover ? (
          <div className="content-view__hero-media">
            <img
              className="content-view__cover"
              src={cover}
              alt=""
              loading="eager"
              fetchpriority="high"
              decoding="async"
            />

            {imageCredit ? (
              <p className="content-view__cover-credit">Foto: {imageCredit}</p>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
